import { useRef, useState } from 'react';
import { CheckCircleOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { SignatureCapture, SignatureDisplay } from '@shared/components/signature/SignatureCapture';

import type { AgreementDocumentView } from './AgreementsSetup.types';

export function AgreementReview({
  document,
  canSign,
  onSign,
}: {
  document: AgreementDocumentView;
  canSign: boolean;
  onSign: (documentId: string, signature: string) => Promise<void>;
}) {
  const [signature, setSignature] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [authorityConfirmed, setAuthorityConfirmed] = useState(false);
  const authorityRequired = document.authorityText !== undefined;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const sign = async () => {
    if (
      !signature ||
      !accepted ||
      (authorityRequired && !authorityConfirmed) ||
      !canSign ||
      document.signed ||
      inFlight.current
    )
      return;
    inFlight.current = true;
    setPending(true);
    setError(false);
    try {
      await onSign(document.id, signature);
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };
  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {document.title}
          </Typography>
          {document.signed && <Chip color="success" size="small" label="Signed" />}
        </Stack>
        <Typography color="text.secondary" variant="body2">
          {document.versionLabel}
        </Typography>
      </Box>
      {document.orderForm && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Order form · Review your services
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These details are included in the agreement below and covered by its signature.
          </Typography>
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
              gap: 1.5,
            }}
          >
            {document.orderForm.map(field => (
              <Box key={field.label} sx={{ display: 'contents' }}>
                <Typography component="dt" color="text.secondary">
                  {field.label}
                </Typography>
                <Typography component="dd" sx={{ m: 0, fontWeight: 600 }}>
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: { xs: 2, md: 4 } }}>
        <Stack spacing={3}>
          {document.sections.map((section, index) => (
            <Box key={`${index}-${section.heading}`}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
                {section.heading}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {section.body}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Divider sx={{ my: 4 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              For JotPsych
            </Typography>
            {document.jotpsychSignature.previewOnly ? (
              <Box sx={{ my: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
                  Synthetic signature preview
                </Typography>
              </Box>
            ) : document.jotpsychSignature.imageDataUri ? (
              <SignatureDisplay
                value={document.jotpsychSignature.imageDataUri}
                label="JotPsych authorized signature"
              />
            ) : (
              <Typography color="text.secondary" sx={{ my: 2 }}>
                {document.jotpsychSignature.required === false
                  ? 'No JotPsych signature is required for this document.'
                  : 'Authorized signature not available'}
              </Typography>
            )}
            <Typography sx={{ fontWeight: 600 }}>{document.jotpsychSignature.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {document.jotpsychSignature.title}
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              For your practice
            </Typography>
            {document.signed ? (
              <>
                <SignatureDisplay
                  value={document.signed.signature}
                  label="Practice administrator signature"
                />
                <Typography sx={{ fontWeight: 600 }}>{document.signed.signerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {document.signed.signedAtLabel}
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                Your signature will appear here.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
      {document.signed ? (
        <Alert severity="success" icon={<CheckCircleOutlined />}>
          Signed agreement. You can return here to read the terms you accepted.
        </Alert>
      ) : !canSign ? (
        <Alert severity="info">
          A practice administrator must review and sign from their own account. You can read the
          prepared agreement here.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Your signature
            </Typography>
            <SignatureCapture
              value={signature}
              disabled={pending}
              onChange={value => {
                setSignature(value);
                setAccepted(false);
                setAuthorityConfirmed(false);
                setError(false);
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  disabled={pending}
                  onChange={event => setAccepted(event.target.checked)}
                />
              }
              label={document.acceptanceText}
            />
            {authorityRequired && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={authorityConfirmed}
                    disabled={pending}
                    onChange={event => setAuthorityConfirmed(event.target.checked)}
                  />
                }
                label={document.authorityText}
              />
            )}
            {error && (
              <Alert severity="error">
                We couldn’t confirm your signature. Retry the same signature or reload saved agreements
                before continuing.
              </Alert>
            )}
            <Box>
              <Button
                variant="contained"
                disabled={
                  !signature || !accepted || (authorityRequired && !authorityConfirmed) || pending
                }
                onClick={() => void sign()}
              >
                {pending ? 'Saving signature…' : `Agree and sign ${document.title}`}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Your signature is submitted only when you choose “Agree and sign.”
            </Typography>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
